import React, { createContext, useContext, useState, useCallback } from "react";
import Joyride, { CallBackProps, Step } from "react-joyride";

type TourContextType = {
  startTour: (tourKey: string) => void;
  registerSteps: (tourKey: string, steps: Step[]) => void;
};

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) throw new Error("useTour must be used within TourProvider");
  return context;
};

const allSteps: Record<string, Step[]> = {};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const registerSteps = (tourKey: string, newSteps: Step[]) => {
    allSteps[tourKey] = newSteps;
  };

  const startTour = (tourKey: string) => {
    const tourSteps = allSteps[tourKey];
    if (tourSteps) {
      setSteps(tourSteps);
      setIsRunning(true);
    }
  };

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, index, type } = data;
    const finishedStatuses = ["finished", "skipped"];

    if (finishedStatuses.includes(status)) {
      setIsRunning(false);
    }
  }, []);

  return (
    <TourContext.Provider value={{ startTour, registerSteps }}>
      <Joyride
        steps={steps}
        run={isRunning}
        callback={handleJoyrideCallback}
        showSkipButton
        showProgress
        continuous
        scrollToFirstStep
        disableScrolling={true}
        disableCloseOnEsc
        disableOverlayClose
        debug
        styles={{
          beaconInner: {
            padding: 0,
            background: "linear-gradient(to bottom, #CCE6FF, #FAFCFF)",
          },
          tooltip: {
            padding: 0,
            background: "linear-gradient(to bottom, #CCE6FF, #FAFCFF)",
            borderRadius: 12,
          },
          options: {
            zIndex: 10000,
            arrowColor: "#1f1f1f",
            overlayColor: "rgba(0,0,0,0.9)",
            primaryColor: "#084784",
          },
          buttonClose: {
            display: "none",
          },
          buttonSkip: { color: "#084784", fontSize: "16px", fontWeight: 500 },
          buttonBack: { color: "#084784", fontSize: "16px", fontWeight: 500 },
          buttonNext: {
            outline: "2px solid transparent",
            outlineOffset: "2px",
          },
          tooltipFooter: {
            paddingBottom: 20,
            paddingLeft: 20,
            paddingRight: 20,
          },
          tooltipContainer: {
            paddingLeft: 20,
            paddingRight: 20,
          },
          tooltipContent: {
            paddingBottom: 0,
          },
        }}
      />
      {children}
    </TourContext.Provider>
  );
};
